/**
 * GDSUIFeedback.js
 * Manages UI feedback elements like loading indicators and notifications.
 */
export class GDSUIFeedback {
    constructor(gdsBuilderElement) {
        this.builderElement = gdsBuilderElement; // The main <gds-builder> element
        this.loadingOverlay = null;
        this.ensureAnimationStyles();
    }

    /**
     * Shows the main loading overlay.
     */
    showLoading() {
        if (!this.loadingOverlay) {
            this.loadingOverlay = document.createElement('div');
            this.loadingOverlay.className = 'loading-overlay fixed inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50';
            this.loadingOverlay.innerHTML = `
                <div class="text-center">
                    <div class="w-12 h-12 border-t-2 border-b-2 border-red-600 rounded-full animate-spin mx-auto"></div>
                    <p class="mt-4 text-gray-600">Loading...</p>
                </div>
            `;
            document.body.appendChild(this.loadingOverlay);
        }
        this.loadingOverlay.style.display = 'flex';
    }

    /**
     * Hides the main loading overlay.
     */
    hideLoading() {
        if (this.loadingOverlay) {
            this.loadingOverlay.style.display = 'none';
        }
    }

    /**
     * Displays an error message toast.
     * @param {string} message - The error message to display.
     */
    showError(message) {
        const errorContainer = this._createToastContainer('error-container', `
            <div class="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg shadow-xl w-full max-w-md">
                <div class="flex">
                    <div class="flex-shrink-0">
                        <svg class="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                        </svg>
                    </div>
                    <div class="ml-3">
                        <p class="text-sm text-red-700">${message}</p>
                    </div>
                </div>
            </div>
        `);
        setTimeout(() => errorContainer.remove(), 5000);
    }

    /**
     * Displays a confirmation message toast.
     * @param {string} message - The confirmation message to display.
     */
    showConfirmation(message) {
        const confirmationContainer = this._createToastContainer('confirmation-container', `
            <div class="bg-green-100 border-l-4 border-green-500 p-4 rounded-lg shadow-xl w-full max-w-md animate-slide-down">
                 <div class="flex">
                    <div class="flex-shrink-0">
                        <svg class="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                           <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                        </svg>
                    </div>
                    <div class="ml-3">
                        <p class="text-sm text-green-700">${message}</p>
                    </div>
                </div>
            </div>
        `);
        setTimeout(() => confirmationContainer.remove(), 3000);
    }

    /**
     * Private helper to create and manage toast containers.
     * @private
     */
    _createToastContainer(id, innerHTML) {
        let container = document.getElementById(id);
        if (container) {
            container.remove(); // Remove existing toast to prevent stacking
        }

        container = document.createElement('div');
        container.id = id;

        // ==== THIS IS THE CORRECTED LINE ====
        // Use inset-x-0 with flex and justify-center to create a full-width centered container.
        container.className = 'fixed top-4 inset-x-0 z-[1001] flex justify-center px-4 pointer-events-none';

        container.innerHTML = `<div class="pointer-events-auto">${innerHTML}</div>`; // Wrap content to make it clickable
        document.body.appendChild(container);
        return container;
    }

    /**
     * Ensures animation styles for toasts are injected into the document head.
     * @private
     */
    ensureAnimationStyles() {
        if (!document.getElementById('gds-builder-animations')) {
            const style = document.createElement('style');
            style.id = 'gds-builder-animations';
            style.textContent = `
                @keyframes slide-down {
                    0% { transform: translateY(-150%); opacity: 0; }
                    100% { transform: translateY(0); opacity: 1; }
                }
                .animate-slide-down {
                    animation: slide-down 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
                }
            `;
            document.head.appendChild(style);
        }
    }
}
